import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import Modal from 'react-native-modal';

import StartRequiredForms from './StartRequiredForms';
import { VerificationStatus } from '../../api/search';
import Colors from '../../constants/Colors';
import FeatureFlags from '../../constants/FeatureFlags';
import Layout from '../../constants/Layout';
import Links from '../../constants/Links';
import useAppNavigation from '../../hooks/useAppNavigation';
import useColorScheme from '../../hooks/useColorScheme';
import { handleLink } from '../../utils/common';
import { LocationCtaButton } from '../Button';
import InfoPopup from '../InfoPopup';
import ModalCloseButton from '../ModalCloseButton';
import Row from '../Row';
import { TextH3 } from '../StyledText';
import { StyleProps, View } from '../Themed';
import LeadForm from '../leadform/LeadForm';

type ButtonsProps = StyleProps & {
  scheduleCallUrl?: string;
  bookTourUrl?: string;
  verificationStatus: VerificationStatus;
  requiresLicensingForms: boolean;
  toursOpen?: boolean;
};

function BookATourButton({
  bookTourUrl,
  toursOpen,
}: {
  bookTourUrl?: string;
  toursOpen?: boolean;
}) {
  const theme = useColorScheme();
  const bookATour = () => (bookTourUrl ? handleLink(bookTourUrl) : undefined);

  const [isModalOpen, setIsModalOpen] = useState(!!toursOpen);
  const navigation = useAppNavigation();

  useEffect(() => {
    if (isModalOpen) {
      navigation.setParams({ tours: true });
    } else {
      navigation.setParams({ tours: undefined });
    }
  }, [isModalOpen]);
  const openBookTourModal = () => setIsModalOpen(true);

  if (FeatureFlags.LEAD_FORM_V2) {
    return (
      <>
        <LocationCtaButton
          style={styles.button}
          left="Book a Tour"
          onPress={openBookTourModal}
        />
        <Modal
          useNativeDriver
          isVisible={isModalOpen}
          onBackdropPress={() => setIsModalOpen(false)}
          style={{
            margin: 0,
            zIndex: 1000,
            maxHeight: '100%',
          }}
          presentationStyle="overFullScreen"
        >
          <View
            style={{
              backgroundColor: Colors[theme].background,
              maxWidth: 600,
              width: '100%',
              padding: 20,
              borderRadius: 10,
              alignSelf: 'center',
              flex: 1,
              ...Platform.select({
                web: {
                  overflowY: 'scroll',
                },
              }),
            }}
          >
            <ModalCloseButton absolute onPress={() => setIsModalOpen(false)} />
            <TextH3 style={{ textAlign: 'center', marginBottom: 10 }}>
              Request a tour
            </TextH3>
            <LeadForm config={{ source: 'REQUEST_TOUR' }} wide />
          </View>
        </Modal>
      </>
    );
  }

  if (!bookTourUrl) {
    return null;
  }

  return (
    <LocationCtaButton
      style={styles.button}
      left="Book a Tour"
      onPress={bookATour}
    />
  );
}

export default function Buttons({
  scheduleCallUrl,
  bookTourUrl,
  verificationStatus,
  requiresLicensingForms,
  toursOpen,
}: ButtonsProps) {
  const theme = useColorScheme();
  return (
    <>
      <Row style={styles.buttons}>
        {!!scheduleCallUrl && (
          <LocationCtaButton
            style={styles.button}
            left="Schedule a Call"
            onPress={() => handleLink(scheduleCallUrl)}
          />
        )}
        <BookATourButton bookTourUrl={bookTourUrl} toursOpen={toursOpen} />
        {Layout.isLargeDevice &&
          verificationStatus === VerificationStatus.VERIFIED &&
          requiresLicensingForms && <StartRequiredForms />}
      </Row>
      {Layout.isMobileDevice &&
        verificationStatus === VerificationStatus.VERIFIED &&
        requiresLicensingForms && (
          <Row style={[styles.buttons, styles.licensingButtons]}>
            <StartRequiredForms />
          </Row>
        )}
      {verificationStatus === VerificationStatus.UNVERIFIED && (
        <Row style={[styles.buttons, styles.licensingButtons]}>
          <LocationCtaButton
            style={[
              styles.button,
              styles.licensingButton,
              { backgroundColor: Colors[theme].accent },
            ]}
            left="Claim This Location"
            onPress={() => handleLink(Links.forms.claim)}
          />
          <InfoPopup text="Is this your daycare? Claim this location to get access to more features and drive enrollment and bookings. Thousands of families visit this website and it's free to claim!" />
        </Row>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  buttons: {
    marginTop: 10,
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  button: {
    marginRight: 10,
    marginBottom: 10,
  },
  licensingButtons: {
    marginTop: 0,
  },
  licensingButton: {
    marginRight: 5,
  },
});
